/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {dlsColor, dlsLayout} from '@ion/dls/src/theme/theme';

// defining the props definition for useLayoutStyle...
export interface UseLayoutStyleProps {
  useExpandCollapse: boolean;
  showProfile: boolean;
  collapsed: boolean;
  backgroundColor: string;
  offsetHeight?: number;
};

export type UseLayoutStyleResult = {
  backgroundColor: string;
  width?: string;
  left?: string
  transition?: string;
  height?: string;
};

/**
 * Defining the useLayoutStyle hook. This returns the style of the layout
 * based on the navigation mode(either collapsed or expanded).
 * 
 * ```typescript
 * 
 *  import {useLayoutStyle} from '@ion/applications/src/layout';
 * 
 *  const styles = useLayoutStyle(params);
 * 
 * ```
 */
export const useLayoutStyle = ({
  offsetHeight,
  collapsed = false,
  showProfile = false,
  useExpandCollapse = false,
  backgroundColor = dlsColor('grey6')
}: UseLayoutStyleProps): UseLayoutStyleResult => {
  const style = useMemo(() => {
    const styles: UseLayoutStyleResult  = {
      backgroundColor : showProfile ? dlsColor('white') : backgroundColor,
      transition      : 'width 0.2s ease-in-out, left 0.2s ease-in-out'
    };
    if (useExpandCollapse) {
      const left   = dlsLayout(`${collapsed ?'collapsedSidebar' : 'sidebar'}`).width;
      styles.width = `calc(100% - ${left}px)`;
      styles.left  = left;
    }
    if (offsetHeight) {
      styles.height = `calc(100vh - ${offsetHeight}px)`;
    }
    return styles;
  }, [
    useExpandCollapse,
    offsetHeight,
    showProfile,
    backgroundColor,
    collapsed
  ]);

  return style;
};

export default useLayoutStyle;
